import env from "@/app/config/env";
import type {
  AppointmentsListType,
  ServiceAppointmentItemType,
  ServiceAppointmentRequestType,
  ServiceCalendarItemType,
  ServiceProductItemType,
  ServiceProductSlotsListType,
  ServiceTypeItemType,
} from "@/schemas/services";
import {
  ServiceAppointmentItemSchema,
  ServiceAppointmentsListSchema,
  ServiceCalendarItemSchema,
  ServiceProductItemSchema,
  ServiceProductSlotsListSchema,
  ServiceTypeItemSchema,
} from "@/schemas/services";
import axios, { type AxiosResponse } from "axios";

export interface ServiceServicesApi {
  fetchAppointments: () => Promise<AppointmentsListType>;
  fetchAppointmentById: (
    appointmentId: string,
  ) => Promise<ServiceAppointmentItemType>;
  deleteAppointment: (appointmentId: string) => Promise<void>;
  createAppointment: (values: ServiceAppointmentRequestType) => Promise<void>;
  fetchServiceTypes: () => Promise<ServiceTypeItemType[]>;
  fetchServiceProducts: () => Promise<ServiceProductItemType[]>;
  fetchServiceProductSlots: (
    serviceProductId: string,
    month: number,
    year: number,
  ) => Promise<ServiceProductSlotsListType>;
  fetchServiceCalendar: (
    serviceProductId: string,
  ) => Promise<ServiceCalendarItemType[]>;

  fetchServiceFileIcs: (
    serviceProductId: string,
  ) => Promise<{ blob: Blob; filename: string }>;
}

export const serviceServicesApi: ServiceServicesApi = {
  fetchAppointments: () => {
    return axios
      .get(`${env.services.portal}/appointments`)
      .then((response: AxiosResponse<unknown>) => {
        return ServiceAppointmentsListSchema.parse(response.data);
      });
  },

  fetchAppointmentById: (appointmentId) => {
    return axios
      .get(`${env.services.portal}/appointments/${appointmentId}`)
      .then((response: AxiosResponse<unknown>) => {
        return ServiceAppointmentItemSchema.parse(response.data);
      });
  },

  deleteAppointment: (appointmentId) => {
    return axios.delete(`${env.services.portal}/appointments/${appointmentId}`);
  },
  createAppointment: (values) => {
    return axios.post(`${env.services.portal}/appointments`, values);
  },

  fetchServiceTypes: () => {
    return axios
      .get(`${env.services.portal}/appointments/services`)
      .then((response: AxiosResponse<unknown[]>) => {
        return (
          response.data?.map((item) => ServiceTypeItemSchema.parse(item)) || []
        );
      });
  },

  fetchServiceProducts: () => {
    return axios
      .get(`${env.services.portal}/appointments/serviceproducts`)
      .then((response: AxiosResponse<unknown[]>) => {
        return (
          response.data?.map((item) => ServiceProductItemSchema.parse(item)) ||
          []
        );
      });
  },

  fetchServiceProductSlots: (serviceProductId, month, year) => {
    return axios
      .get(
        `${env.services.portal}/appointments/serviceproducts/${serviceProductId}/slots`,
        {
          params: {
            month,
            year,
          },
        },
      )
      .then((response: AxiosResponse<unknown>) => {
        return ServiceProductSlotsListSchema.parse(response.data);
      });
  },
  fetchServiceCalendar: (serviceProductId) => {
    return axios
      .get(`${env.services.portal}/service/${serviceProductId}/schedule`)
      .then((response: AxiosResponse<unknown[]>) => {
        return (
          response.data?.map((item) => ServiceCalendarItemSchema.parse(item)) ||
          []
        );
      });
  },
  fetchServiceFileIcs: (serviceProductId) => {
    return axios
      .get(`${env.services.portal}/appointments/${serviceProductId}/ics`, {
        responseType: "blob",
      })
      .then((response: AxiosResponse<Blob>) => {
        const contentDisposition = response.headers["content-disposition"];
        let filename = "Appointment.ics";

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
          );
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, "");
          } else {
            const filenameStarMatch = contentDisposition.match(
              /filename\*=UTF-8''(.+)/,
            );
            if (filenameStarMatch && filenameStarMatch[1]) {
              filename = decodeURIComponent(filenameStarMatch[1]);
            }
          }
        }

        return {
          blob: response.data,
          filename,
        };
      });
  },
};
